import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { adminGetAmendment, adminListAmendments, adminRespondToAmendment } from '../../api/adminBookings';
import { amendmentTypeLabel } from '../../api/amendments';
import AdminShell, { useAdminAccess } from './AdminShell';
import { can } from './access';
import { describeStatus, fmtDateTime, fmtDay, money, productLabel, productTypes, statusClass } from './bookingFormat';

const tabs = [['Open', 'New / replied'], ['InProgress', 'In progress'], ['AwaitingCustomer', 'Awaiting customer'], ['Completed', 'Completed'], ['Rejected', 'Rejected'], ['', 'All']];
const countKey = { Open: 'open', InProgress: 'inProgress', AwaitingCustomer: 'awaitingCustomer', Completed: 'completed', Rejected: 'rejected' };

/** Customers' change requests (cancellations, date changes, name corrections…) and the admin's replies. */
export default function AdminAmendmentsPage() {
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const [params, setParams] = useSearchParams();
  const status = params.has('status') ? params.get('status') : 'Open';
  const product = params.get('product') || '';
  const search = params.get('search') || '';
  const page = Number(params.get('page')) || 1;
  const openId = params.get('id');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState(search);

  const load = useCallback(async () => {
    try {
      setError('');
      setData(await adminListAmendments(auth.accessToken, { status, product, search, page, pageSize: 25 }));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [auth?.accessToken, status, product, search, page]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const set = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => (v === null ? next.delete(k) : next.set(k, v)));
    if (!('page' in changes)) next.delete('page');
    setParams(next);
  };

  return (
    <AdminShell title="Amendment requests" subtitle="Change requests customers raised on their bookings. Check with the supplier, then reply — the customer is emailed and sees the reply in My Trips.">
      <div className="admin-nav admin-tabs" role="tablist">
        {tabs.map(([value, label]) => (
          <button key={label} type="button" role="tab" aria-selected={status === value} className={status === value ? 'active' : ''} onClick={() => set({ status: value })}>
            {label}{value && data ? ` (${data.counts[countKey[value]]})` : ''}
          </button>
        ))}
      </div>
      <form className="admin-filters" onSubmit={(e) => { e.preventDefault(); set({ search: searchText.trim() || null }); }}>
        <label className="field">
          <span>Product</span>
          <select value={product} onChange={(e) => set({ product: e.target.value || null })}>
            <option value="">All products</option>
            {productTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="field grow">
          <span>Search</span>
          <input type="search" placeholder="Booking reference, customer or message" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </label>
        <button type="submit" className="btn btn-primary btn-sm">Search</button>
      </form>

      {error && <p className="api-message error" role="alert">{error}</p>}
      {!data && !error && <p role="status">Loading requests…</p>}
      {data && (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Updated</th><th>Request</th><th>Booking</th><th>Customer</th><th>Message</th><th>Status</th></tr></thead>
              <tbody>
                {data.amendments.map(({ request: a, ...row }) => (
                  <tr key={a.id} className="admin-click-row" onClick={() => set({ id: a.id, page: String(page) })}>
                    <td>{fmtDateTime(a.updatedAt)}</td>
                    <td><b>{amendmentTypeLabel[a.type] || a.type}</b></td>
                    <td>{productLabel[a.productType] || a.productType} · {a.supplierBookingReference}<br /><span className="muted small">{a.bookingSummary || ''}{a.travelDate ? ` · ${fmtDay(a.travelDate)}` : ''}</span></td>
                    <td>{row.customerName}<br /><span className="muted small">{row.customerEmail}</span></td>
                    <td className="admin-wrap">{lastText(a)}</td>
                    <td><span className={statusClass(a.status)}>{describeStatus(a.status)}</span></td>
                  </tr>
                ))}
                {!data.amendments.length && <tr><td colSpan="6">No requests here.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="admin-pagination">
            <span>{data.totalCount} requests</span>
            <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => set({ page: String(page - 1) })}>Previous</button>
            <span>Page {page}</span>
            <button className="btn btn-outline btn-sm" disabled={page * data.pageSize >= data.totalCount} onClick={() => set({ page: String(page + 1) })}>Next</button>
          </div>
        </>
      )}

      {openId && <AmendmentDetail id={openId} token={auth.accessToken} onClose={() => set({ id: null, page: String(page) })} onSaved={load} />}
    </AdminShell>
  );
}

const lastText = (a) => {
  const last = a.messages[a.messages.length - 1];
  const text = last ? `${last.fromAdmin ? 'You: ' : ''}${last.text}` : a.message;
  return text.length > 140 ? `${text.slice(0, 140)}…` : text;
};

function AmendmentDetail({ id, token, onClose, onSaved }) {
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ status: 'InProgress', message: '', charges: '', notifyCustomer: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    adminGetAmendment(token, id)
      .then((d) => {
        if (!active) return;
        setItem(d);
        setForm((f) => ({ ...f, status: d.request.status === 'Open' ? 'InProgress' : d.request.status, charges: d.request.charges ?? '' }));
      })
      .catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, [token, id]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const saved = await adminRespondToAmendment(token, id, {
        status: form.status,
        message: form.message.trim() || null,
        charges: form.charges === '' ? null : Number(form.charges),
        notifyCustomer: form.notifyCustomer,
      });
      setItem(saved);
      setForm((f) => ({ ...f, message: '' }));
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const a = item?.request;
  return (
    <div className="overlay" onClick={onClose}>
      <div className="overlay-card modal admin-detail" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Amendment request">
        <div className="admin-detail-head">
          <h3>{a ? `${amendmentTypeLabel[a.type] || a.type} · ${a.supplierBookingReference}` : 'Request'}</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {error && <p className="api-message error" role="alert">{error}</p>}
        {!item && !error && <p role="status">Loading…</p>}
        {a && (
          <>
            <div className="kv-grid">
              <div><span className="muted small">Status</span><b><span className={statusClass(a.status)}>{describeStatus(a.status)}</span></b></div>
              <div><span className="muted small">Booking</span><b>{productLabel[a.productType] || a.productType} · <span className={statusClass(item.bookingStatus)}>{describeStatus(item.bookingStatus)}</span></b></div>
              <div><span className="muted small">Trip</span><b>{a.bookingSummary || '—'}{a.travelDate ? ` · ${fmtDay(a.travelDate)}` : ''}</b></div>
              <div><span className="muted small">Supplier</span><b>{item.supplierCode} · {a.supplierBookingReference}</b></div>
              <div><span className="muted small">Customer</span><b>{item.customerName}</b></div>
              <div><span className="muted small">Contact</span><b><a className="link" href={`mailto:${item.customerEmail}`}>{item.customerEmail}</a>{item.customerPhone && <> · <a href={`tel:${item.customerPhone}`}>{item.customerPhone}</a></>}</b></div>
              <div><span className="muted small">Sale / supplier price</span><b>{money(item.saleAmount)} / {money(item.supplierAmount)}</b></div>
              <div><span className="muted small">Quoted charges</span><b>{a.charges != null ? money(a.charges) : '—'}</b></div>
            </div>
            {can(user, 'Bookings') && <Link className="link small" to={`/admin/bookings?booking=${a.bookingId}`}>Open booking, payments and refunds →</Link>}

            <div className="amend-thread">
              <div className="amend-msg customer"><span className="muted small">{item.customerName} · {fmtDateTime(a.createdAt)}</span><p>{a.message}</p></div>
              {a.messages.map((m) => (
                <div key={m.id} className={`amend-msg ${m.fromAdmin ? 'admin' : 'customer'}`}>
                  <span className="muted small">{m.authorName} · {fmtDateTime(m.createdAt)}</span><p>{m.text}</p>
                </div>
              ))}
            </div>

            <form className="admin-form" onSubmit={submit}>
              <label className="field wide"><span>Reply to customer</span>
                <textarea rows="4" maxLength="2000" value={form.message} placeholder="E.g. The airline charges ₹2,500 to move you to 12 Nov. Reply to confirm and we'll make the change."
                  onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </label>
              <div className="admin-filters">
                <label className="field"><span>Set status</span>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {tabs.filter(([v]) => v).map(([value]) => <option key={value} value={value}>{describeStatus(value)}</option>)}
                  </select>
                </label>
                <label className="field"><span>Charges to customer (₹)</span>
                  <input type="number" min="0" step="any" value={form.charges} onChange={(e) => setForm({ ...form, charges: e.target.value })} />
                </label>
                <label className="check"><input type="checkbox" checked={form.notifyCustomer} onChange={(e) => setForm({ ...form, notifyCustomer: e.target.checked })} /> Email the customer</label>
                <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Send update'}</button>
              </div>
              {form.status === 'Completed' && a.type === 'Cancellation' && (
                <p className="muted small">After cancelling with the supplier offline, record the refunds on the booking so profit reports stay right.</p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
